import type { CrmNoteType } from '@plato/data/prisma';
import {
    differenceInCalendarDays,
    format,
    formatISO,
    intlFormat,
    isToday,
    min,
    parseISO,
    roundToNearestMinutes,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { CalendarIcon, CalendarPlus, ClockIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CustomerIcon } from '../../../assets/icons/customer-icon.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import Text from '../../../components/ui/text.tsx';
import TimePicker from '../../../components/ui/time-picker.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmEventDTO } from '../../../server/dto/crm-event-dto.ts';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AnalyticsEventPayload } from '../../analytics/types/analytics-events.ts';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import type { AIActionType } from '../../rich-text/hooks/use-improve-writing.ts';
import { CustomerSelect } from '../../shared/components/form-items-with-fetch/customer-select.tsx';
import { NotReachedBanner } from './not-reached-banner.tsx';
import { NoteFormFields } from './note-form-fields.tsx';
import { NoteTypeSelector } from './note-type-selector.tsx';

export type CreateNoteFormProps = {
    customer?: CustomerDTO;
    event?: CrmEventDTO;
    onCancel?: () => void;
    onSuccess?: () => void;
    onIsDirtyChange?: (isDirty: boolean) => void;
    analyticsSource?: AnalyticsEventPayload<'customer_interaction_created'>['page_source'];
};

const getMaxDate = (): Date => roundToNearestMinutes(new Date(), { roundingMethod: 'floor', nearestTo: 15 });

const getDefaultOccurredAt = (event: CrmEventDTO | undefined): string => {
    if (!event) {
        return getMaxDate().toISOString();
    }

    return new Date(`${event.date}T${event.start_time ?? '12:00:00'}`).toISOString();
};

const getOccurredAtFromEvent = (event: CrmEventDTO): string =>
    fromZonedTime(`${event.date}T${event.start_time ?? '12:00:00'}`, 'Europe/Berlin').toISOString();

const PrefillRail = (): ReactNode => (
    <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-1"
        style={{ background: 'linear-gradient(180deg, #A5B4FC, #C4B5FD)' }}
    />
);

export const CreateNoteForm = ({
    customer,
    event,
    onSuccess,
    onCancel,
    onIsDirtyChange,
    analyticsSource,
}: CreateNoteFormProps): ReactNode => {
    const { t, locale } = useLocale();
    const { toast } = useToast();
    const utils = api.useUtils();
    const { currentSessionId } = useVoiceRecording();
    const { enableFileUpload, enableCreateNextEventFakeDoor } = useAppConfig();

    const sendEvent = useAnalytics();
    const [isAIBusy, setIsAIBusy] = useState<boolean>(false);
    const [aiSessionId, setAISessionId] = useState<string | undefined>(undefined);

    const handleAIInteraction = (action: AIActionType, sessionId: string): void => {
        switch (action) {
            case 'accept':
                // Store the session ID for any AI interaction (we'll use it when the note is created)
                setAISessionId(sessionId);
                setIsAIBusy(false);
                break;
            case 'request':
            case 'regenerate':
                setIsAIBusy(true);
                break;
            case 'reject':
                setIsAIBusy(false);
                break;
            default:
                // no-op for unknown actions
                break;
        }
    };

    const [noteType, setNoteType] = useState<CrmNoteType>('VISIT');

    const [overrodeDate, setOverrodeDate] = useState(false);
    const [overrodeTime, setOverrodeTime] = useState(false);

    const defaultValues = {
        title: undefined,
        customerId: customer?.customerId,
        eventId: event?.event_id,
        occurredAt: getDefaultOccurredAt(event),
        attachments: undefined,
        interactionHappened: true,
    };

    const {
        control,
        handleSubmit,
        clearErrors,
        watch,
        setValue,
        formState: { errors: formErrors, isDirty },
    } = useForm<Omit<RouterInput['platoUser']['command']['notes']['create'], 'noteType'>>({
        defaultValues,
    });

    const watchedCustomerId = watch('customerId');
    const watchedInteractionHappened = watch('interactionHappened');
    const notReachedMode = watchedInteractionHappened === false;

    const shouldSuggestDate = !event && !!watchedCustomerId;

    const lastEventQuery = api.platoUser.query.events.getLastUnloggedCustomerEvent.useQuery(
        { customerId: watchedCustomerId },
        { enabled: shouldSuggestDate },
    );

    const suggestedEvent = shouldSuggestDate ? (lastEventQuery.data ?? null) : null;

    const handleNoteTypeChange = (value: CrmNoteType): void => {
        setNoteType(value);
        setValue('interactionHappened', value === 'VISIT' ? true : null);
    };

    useEffect(() => {
        clearErrors('title');
    }, [noteType, clearErrors]);

    useEffect(() => {
        onIsDirtyChange?.(isDirty);
    }, [isDirty, onIsDirtyChange]);

    useEffect(() => {
        setOverrodeDate(false);
        setOverrodeTime(false);
    }, [watchedCustomerId]);

    useEffect(() => {
        if (noteType === 'NOTE' || overrodeDate || overrodeTime) {
            return;
        }

        setValue(
            'occurredAt',
            suggestedEvent ? getOccurredAtFromEvent(suggestedEvent) : getDefaultOccurredAt(event),
        );
    }, [suggestedEvent, noteType, overrodeDate, overrodeTime, event, setValue]);

    useEffect(() => {
        if (!shouldSuggestDate || !lastEventQuery.isFetched || suggestedEvent) {
            return;
        }

        sendEvent('log_date_prefill_unavailable', { customer_id: watchedCustomerId });
    }, [shouldSuggestDate, lastEventQuery.isFetched, suggestedEvent, sendEvent, watchedCustomerId]);

    const showDateRail = !!suggestedEvent && noteType !== 'NOTE' && !overrodeDate;
    const showTimeRail = !!suggestedEvent && noteType !== 'NOTE' && !overrodeTime;
    const showPrefillHint = showDateRail || showTimeRail;

    const createMutation = api.platoUser.command.notes.create.useMutation({
        onSuccess: async (note) => {
            // Invalidate activity feed queries to refresh the data and last logged event to not suggest an already logged event
            await utils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
            await utils.platoUser.query.events.getLastUnloggedCustomerEvent.invalidate();

            toast({
                icon: 'success',
                variant: 'default',
                title: t('notes__form_create_toast__success_title', { ns: 'notes' }),
                createdLink: <LinkText href={`/customers/${note.customerId}/activity`} />,
            });

            sendEvent('customer_interaction_created', {
                note_id: note.noteId,
                customer_id: note.customerId,
                note_type: noteType,
                ai_session_id: aiSessionId,
                page_source: analyticsSource,
                not_reached_mode: notReachedMode,
                had_reason: !!note.title,
            });

            if (suggestedEvent && noteType !== 'NOTE') {
                const daysAgo = differenceInCalendarDays(new Date(), parseISO(suggestedEvent.date));

                if (overrodeDate || overrodeTime) {
                    sendEvent('log_date_prefill_overridden', {
                        event_id: suggestedEvent.event_id,
                        days_ago: daysAgo,
                        fields_changed:
                            overrodeDate && overrodeTime ? 'both' : overrodeDate ? 'date' : 'time',
                    });
                } else {
                    sendEvent('log_date_prefill_used', {
                        event_id: suggestedEvent.event_id,
                        days_ago: daysAgo,
                        interaction_type: noteType === 'VISIT' ? 'besuch' : 'anruf',
                    });
                }
            }

            onSuccess?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const onSubmit = handleSubmit((data) => {
        createMutation.mutate({
            ...data,
            occurredAt: noteType === 'NOTE' ? undefined : data.occurredAt,
            eventId: noteType === 'VISIT' ? data.eventId : undefined,
            noteType,
            interactionHappened: noteType === 'VISIT' ? data.interactionHappened : null,
        });
    });

    const handleCreateNextEventClick = (): void => {
        sendEvent('create_next_event_from_log', {
            customer_id: watchedCustomerId,
        });
        toast({
            icon: 'info',
            title: t('fake_door_next_event_toast_title', { ns: 'events' }),
            description: t('fake_door_next_event_toast_description', { ns: 'events' }),
        });
    };

    return (
        <DialogForm className="sm:max-w-2xl">
            <DialogForm.Header title={t('notes__form_create_title', { ns: 'notes' })} />
            <DialogForm.Content>
                {!event && <NoteTypeSelector value={noteType} onChange={handleNoteTypeChange} />}
                {noteType === 'VISIT' && (
                    <Controller
                        name="interactionHappened"
                        control={control}
                        render={({ field }) => (
                            <NotReachedBanner
                                active={field.value === false}
                                onChange={(active) => {
                                    field.onChange(!active);
                                }}
                            />
                        )}
                    />
                )}
                <NoteFormFields
                    noteType={noteType}
                    control={control}
                    errors={formErrors}
                    onAIInteraction={notReachedMode ? undefined : handleAIInteraction}
                    placeholderOverride={
                        notReachedMode
                            ? t('notes__not_reached__reason_placeholder', { ns: 'notes' })
                            : undefined
                    }
                />
                <DialogForm.ItemList grid>
                    {/* Customer Field */}
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label
                            htmlFor={customer ? undefined : 'customerId'}
                            icon={CustomerIcon}
                            required={!customer}
                        >
                            {t('notes__form__customer_label', { ns: 'notes' })}
                        </DialogForm.Label>
                        {customer ? (
                            <DialogForm.Body className="flex flex-col gap-1 py-1.5 px-2">
                                <Text color="neutral-950">{customer.customerName}</Text>
                            </DialogForm.Body>
                        ) : (
                            <DialogForm.Body className="flex flex-col gap-1">
                                <Controller
                                    name="customerId"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t('form__customer_required', { ns: 'common' }),
                                        },
                                    }}
                                    render={({ field: { value, onChange } }) => (
                                        <CustomerSelect
                                            value={value}
                                            onChange={onChange}
                                            className="w-full"
                                            variant="inline"
                                            error={!!formErrors.customerId}
                                            tracking={{
                                                context: 'note_customer_select',
                                                context_entity_type: 'note',
                                            }}
                                        />
                                    )}
                                />
                                <DialogForm.Error error={formErrors.customerId} />
                            </DialogForm.Body>
                        )}
                    </DialogForm.Item>

                    {event && noteType === 'VISIT' && (
                        <DialogForm.Item className="items-start">
                            <DialogForm.Label icon={CalendarIcon}>
                                {t('notes__form__event_label', { ns: 'notes' })}
                            </DialogForm.Label>
                            <DialogForm.Body className="py-1.5 px-2">
                                <Text color="neutral-950">{event.title ?? event.date}</Text>
                            </DialogForm.Body>
                        </DialogForm.Item>
                    )}

                    {/* Date and Time for VISIT and PHONE_CALL */}
                    {noteType !== 'NOTE' && (
                        <>
                            {/* Reserve the row whenever a prefill happened (suggestedEvent) and only
                                toggle the text's visibility, so editing the date/time doesn't make the
                                form jump. With no suggestion the row isn't rendered (no reserved space). */}
                            {!!suggestedEvent && (
                                <div className="col-span-2 flex items-center justify-end">
                                    <span
                                        className="text-xs font-medium"
                                        style={{
                                            backgroundImage: 'linear-gradient(to right, #4C74E6, #8D54E9)',
                                            WebkitBackgroundClip: 'text',
                                            backgroundClip: 'text',
                                            color: 'transparent',
                                            visibility: showPrefillHint ? 'visible' : 'hidden',
                                        }}
                                    >
                                        {t('notes__form__date_prefill_source', { ns: 'notes' })}
                                    </span>
                                </div>
                            )}

                            {/* Date Field */}
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label
                                    htmlFor={event ? undefined : 'businessDate'}
                                    icon={CalendarIcon}
                                    required={!event}
                                >
                                    {t('notes__form__date_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                {event ? (
                                    <DialogForm.Body className="py-1.5 px-2">
                                        <Text color="neutral-950">
                                            {intlFormat(
                                                new Date(event.date),
                                                { month: 'long', day: 'numeric', year: 'numeric' },
                                                { locale },
                                            )}
                                        </Text>
                                    </DialogForm.Body>
                                ) : (
                                    <DialogForm.Body className="relative flex flex-col gap-1">
                                        {showDateRail && <PrefillRail />}
                                        <Controller
                                            name="occurredAt"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    disableIcon
                                                    mode="single"
                                                    value={field.value ? new Date(field.value) : new Date()}
                                                    valuePrefix=""
                                                    onChange={(value) => {
                                                        if (!value) {
                                                            return;
                                                        }

                                                        const baseDate = field.value
                                                            ? new Date(field.value)
                                                            : new Date();

                                                        const timePart = formatISO(baseDate, {
                                                            representation: 'time',
                                                        });

                                                        const datePart = formatISO(value, {
                                                            representation: 'date',
                                                        });

                                                        const nextDate = new Date(datePart + 'T' + timePart);

                                                        field.onChange(
                                                            min([getMaxDate(), nextDate]).toISOString(),
                                                        );
                                                        setOverrodeDate(true);
                                                    }}
                                                    disabled={{ after: new Date() }}
                                                    className="w-full"
                                                />
                                            )}
                                        />
                                    </DialogForm.Body>
                                )}
                            </DialogForm.Item>

                            {/* Time Field */}
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label
                                    htmlFor={event ? undefined : 'businessTime'}
                                    icon={ClockIcon}
                                    required={!event}
                                >
                                    {t('notes__form__time_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                {event ? (
                                    <DialogForm.Body className="py-1.5 px-2">
                                        <Text color="neutral-950">
                                            {intlFormat(
                                                new Date(`${event.date}T${event.start_time ?? '12:00:00'}`),
                                                { hour: 'numeric', minute: 'numeric', hour12: false },
                                                { locale },
                                            )}
                                        </Text>
                                    </DialogForm.Body>
                                ) : (
                                    <DialogForm.Body className="relative flex flex-col gap-1">
                                        {showTimeRail && <PrefillRail />}
                                        <Controller
                                            name="occurredAt"
                                            control={control}
                                            render={({ field }) => (
                                                <TimePicker
                                                    value={
                                                        field.value
                                                            ? format(new Date(field.value), 'HH:mm:ss')
                                                            : null
                                                    }
                                                    onChange={(value) => {
                                                        const baseDate = field.value
                                                            ? new Date(field.value)
                                                            : new Date();

                                                        const datePart = formatISO(baseDate, {
                                                            representation: 'date',
                                                        });

                                                        const nextDate = new Date(datePart + 'T' + value);

                                                        field.onChange(nextDate.toISOString());
                                                        setOverrodeTime(true);
                                                    }}
                                                    max={
                                                        !field.value || isToday(field.value)
                                                            ? format(getMaxDate(), 'HH:mm:ss')
                                                            : undefined
                                                    }
                                                    placeholder={t('time_picker__placeholder', {
                                                        ns: 'common',
                                                    })}
                                                />
                                            )}
                                        />
                                    </DialogForm.Body>
                                )}
                            </DialogForm.Item>
                        </>
                    )}
                    {enableFileUpload && (
                        <Controller
                            name="attachments"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <FileUploader
                                    value={value ?? []}
                                    onChange={onChange}
                                    analyticsContext="note"
                                />
                            )}
                        />
                    )}
                </DialogForm.ItemList>
                {enableCreateNextEventFakeDoor && (
                    <div className="self-start">
                        <Button variant="secondary" icon={CalendarPlus} onClick={handleCreateNextEventClick}>
                            {t('fake_door_next_event_button', { ns: 'events' })}
                        </Button>
                    </div>
                )}
            </DialogForm.Content>
            <DialogForm.SubmitBlock
                onCancel={onCancel}
                onSubmit={() => {
                    void onSubmit();
                }}
                submitText={notReachedMode ? t('notes__not_reached__submit', { ns: 'notes' }) : undefined}
                disabled={createMutation.isPending || isAIBusy || !!currentSessionId}
            />
        </DialogForm>
    );
};

type NoteFormDialogProps = ModalInnerProps & CreateNoteFormProps;

export const NoteFormDialog = DialogStateManager.create<NoteFormDialogProps>(({ onSuccess, ...props }) => {
    const { visible, remove } = useDialog();
    const [isDirty, setIsDirty] = useState(false);
    const { confirmDiscard } = useDiscardChangesWarning(isDirty);

    const handleClose = (): void => {
        confirmDiscard(remove);
    };

    return (
        <Dialog open={visible} showClose onClose={handleClose}>
            <CreateNoteForm
                onCancel={handleClose}
                onSuccess={() => {
                    remove();
                    onSuccess?.();
                }}
                onIsDirtyChange={setIsDirty}
                {...props}
            />
        </Dialog>
    );
});
